import { useState, useEffect, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import 'moment/locale/ar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import PageContent from '../../components/templates/PageContent'
import Layout from 'containers/Layout'
import LoadingIcon from '../../components/atoms/LoadingIcon'
import Alert from '../../components/atoms/Alert'
import apiGet from '../../lib/network/apiGet'
import useModal from '../../hooks/useModal'
import _, { toLower } from 'lodash'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import { useTranslation } from 'react-i18next'
import { TruckIcon, ClipboardListIcon, CurrencyDollarIcon, BadgeCheckIcon } from '@heroicons/react/outline'

const statTone = {
  emerald: { bar: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-900 ring-emerald-200/80' },
  sky: { bar: 'bg-sky-500', pill: 'bg-sky-50 text-sky-900 ring-sky-200/70' },
  rose: { bar: 'bg-rose-500', pill: 'bg-rose-50 text-rose-900 ring-rose-200/70' },
  amber: { bar: 'bg-amber-500', pill: 'bg-amber-50 text-amber-900 ring-amber-200/70' },
  teal: { bar: 'bg-teal-500', pill: 'bg-teal-50 text-teal-900 ring-teal-200/70' },
  slate: { bar: 'bg-slate-400', pill: 'bg-slate-100 text-slate-900 ring-slate-200/80' },
  violet: { bar: 'bg-violet-500', pill: 'bg-violet-50 text-violet-900 ring-violet-200/70' },
}

const fleetKeyTone = key => ({ active: 'emerald', booked: 'sky', 'out of service': 'rose', inProgress: 'amber' }[key] || 'slate')

const bookingKeyTone = key =>
  ({ pending: 'amber', confirmed: 'emerald', completed: 'teal', rejected: 'rose', cancelled: 'slate', failedPayment: 'rose', inProgress: 'violet' }[key] || 'slate')

const paymentKeyTone = key =>
  ({
    paid: 'emerald',
    unpaid: 'amber',
    failure: 'rose',
    refund: 'violet',
    partialpaid: 'sky',
    waiting_for_payment_approval: 'amber',
  }[key] || 'slate')

const DashboardStatRow = ({ label, value, tone = 'slate' }) => {
  const c = statTone[tone] || statTone.slate
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100/90 bg-gradient-to-r from-white to-slate-50/60 px-3 py-2.5 shadow-sm ring-1 ring-slate-900/[0.03] transition-all hover:border-slate-200/80 hover:from-white hover:to-white hover:shadow-md">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className={classNames('h-8 w-1 shrink-0 rounded-full', c.bar)} aria-hidden />
        <span className="truncate text-sm font-medium text-slate-800">{label}</span>
      </div>
      <span className={classNames('inline-flex min-w-[2.75rem] shrink-0 items-center justify-center rounded-lg px-2.5 py-1 text-sm font-bold tabular-nums ring-1', c.pill)}>
        {value}
      </span>
    </div>
  )
}

const createUTCLocalizer = () => {
  const localizer = momentLocalizer(moment)

  const originalFormat = localizer.format
  localizer.format = (value, format, culture) => {
    return originalFormat(moment.utc(value), format, culture)
  }

  return localizer
}

const localizer = createUTCLocalizer()

const modalSectionClass = 'rounded-xl border border-slate-200/70 bg-white p-4 shadow-sm sm:p-5'
const modalHeadingClass = 'mb-4 border-b border-slate-100 pb-3 text-sm font-bold uppercase tracking-[0.12em] text-slate-500'

const CompanyDashboard = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const userFromStore = useSelector(state => state?.user?.user)
  const companyBranchFilterId = useSelector(state => _.get(state, 'settings.companyBranchFilterId'))
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const [rejectLoading, setRejectLoading] = useState(false)
  const [dashboardStats, setDashboardStats] = useState(null)
  const [bookingQueue, setBookingQueue] = useState([])
  const [queueMeta, setQueueMeta] = useState(null)
  const [queueLoading, setQueueLoading] = useState(false)

  // Set moment locale based on selected language
  useEffect(() => {
    moment.locale(i18n.language === 'ar' ? 'ar' : 'en')
  }, [i18n.language])

  // Calendar messages for translation
  const calendarMessages = {
    today: t('companyDashboard.calendar.today'),
    previous: t('companyDashboard.calendar.previous'),
    next: t('companyDashboard.calendar.next'),
    month: t('companyDashboard.calendar.month'),
    week: t('companyDashboard.calendar.week'),
    day: t('companyDashboard.calendar.day'),
    agenda: t('companyDashboard.calendar.agenda'),
    date: t('companyDashboard.calendar.date'),
    time: t('companyDashboard.calendar.time'),
    event: t('companyDashboard.calendar.event'),
    allDay: t('companyDashboard.calendar.allDay'),
    noEventsInRange: t('companyDashboard.calendar.noEventsInRange'),
    showMore: total => t('companyDashboard.calendar.showMore') + ` (${total})`,
  }

  const fetchDashboardStats = useCallback(async () => {
    const res = await apiGet('company-dashboard/stats', {}, false)
    if (res?.success && res.data) {
      setDashboardStats(res.data)
    }
  }, [companyBranchFilterId])

  const fetchBookingQueue = useCallback(async () => {
    setQueueLoading(true)
    try {
      const res = await apiGet('company-dashboard/bookings-queue', { limit: 50, newWithinHours: 72 }, false)
      if (res?.success && res.data) {
        setBookingQueue(res.data.bookings || [])
        setQueueMeta(res.data.meta || null)
      } else {
        setBookingQueue([])
        setQueueMeta(null)
      }
    } catch {
      setBookingQueue([])
      setQueueMeta(null)
    } finally {
      setQueueLoading(false)
    }
  }, [companyBranchFilterId])

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiGet('/company-active/bookings')
      if (response?.data) {
        setBookings(response.data)
      }
    } catch (err) {
      setError(t('companyDashboard.messages.failedToFetchBookings'))
    } finally {
      setLoading(false)
      fetchDashboardStats()
      fetchBookingQueue()
    }
  }, [t, fetchDashboardStats, fetchBookingQueue, companyBranchFilterId])

  useEffect(() => {
    fetchBookings()
  }, [fetchBookings])

  const handleUpdateBooking = (bookingId, status) => {
    const title = status === 'confirmed' ? t('companyDashboard.messages.acceptBookingTitle') : t('companyDashboard.messages.rejectBookingTitle')
    const message = status === 'confirmed' ? t('companyDashboard.messages.acceptBookingMessage') : t('companyDashboard.messages.rejectBookingMessage')

    const confirmMethod = status === 'confirmed' ? notify.alertConfirm : notify.deleteConfirm

    confirmMethod({
      title,
      message,
      onConfirm: async () => {
        try {
          if (status === 'confirmed') {
            setAcceptLoading(true)
          } else if (status === 'rejected') {
            setRejectLoading(true)
          }

          const response = await apiPost(`booking/${bookingId}`, { status })

          if (response?.success) {
            const successMessage = status === 'confirmed' ? t('companyDashboard.messages.bookingAcceptedSuccess') : t('companyDashboard.messages.bookingRejectedSuccess')
            notify.success(successMessage)

            setBookings(prevBookings => prevBookings.map(booking => (booking._id === bookingId ? { ...booking, status } : booking)))

            if (selectedBooking && selectedBooking._id === bookingId) {
              setSelectedBooking(prev => ({ ...prev, status }))
            }
            fetchBookings()
          }
        } catch (error) {
          notify.error(t('companyDashboard.messages.failedToUpdateBooking'))
          fetchBookings()
        } finally {
          setAcceptLoading(false)
          setRejectLoading(false)
        }
      },
    })
  }

  const events = bookings.map((booking, index) => {
    const startDate = moment.utc(booking.rentalStartDate)
    const endDate = moment.utc(booking.rentalEndDate)

    // Get make and model based on language
    const isArabic = i18n.language === 'ar'
    const make = isArabic
      ? booking.rentalCar?.car?.make?.makeAr || booking.car?.make?.makeAr || booking.rentalCar?.car?.make?.make || booking.car?.make?.make || 'Unknown'
      : booking.rentalCar?.car?.make?.make || booking.car?.make?.make || 'Unknown'
    const model = isArabic
      ? booking.rentalCar?.car?.model?.modalAr || booking.car?.model?.modalAr || booking.rentalCar?.car?.model?.modal || booking.car?.model?.modal || 'Unknown'
      : booking.rentalCar?.car?.model?.modal || booking.car?.model?.modal || 'Unknown'

    return {
      id: booking._id,
      title: `${make} ${model}`,
      start: startDate.toDate(),
      end: endDate.toDate(),
      resource: {
        booking,
        status: booking.status,
        paymentStatus: booking.paymentStatus,
        user: booking.user,
        branch: booking.branch,
        totalAmount: booking.totalAmount,
        utcStart: startDate,
        utcEnd: endDate,
      },
    }
  })

  const eventStyleGetter = event => {
    const status = toLower(event.resource?.status || '')
    let backgroundColor = '#334155'

    switch (status) {
      case 'confirmed':
        backgroundColor = '#059669'
        break
      case 'pending':
        backgroundColor = '#64748b'
        break
      case 'completed':
        backgroundColor = '#0d9488'
        break
      case 'rejected':
      case 'cancelled':
        backgroundColor = '#dc2626'
        break
      default:
        backgroundColor = '#475569'
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.95,
        color: 'white',
        border: '0px',
        display: 'block',
        height: 'auto',
        minHeight: '20px',
        marginBottom: '2px',
        fontSize: '11px',
        lineHeight: '1.25',
        padding: '3px 6px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
      },
    }
  }

  const handleSelectEvent = event => {
    setSelectedBooking(event.resource.booking)

    openBookingModal()
  }

  const handleSelectSlot = () => {}

  const companyDisplayName = useMemo(() => {
    const company = userFromStore?.company
    if (!company) return t('companyDashboard.companyName')
    const isAr = i18n.language === 'ar'
    const raw = isAr && company.nameAr ? company.nameAr : company.name
    const trimmed = _.trim(raw != null ? String(raw) : '')
    return trimmed || t('companyDashboard.companyName')
  }, [userFromStore?.company, i18n.language, t])

  const getCarDisplayName = useCallback(
    booking => {
      const isAr = i18n.language === 'ar'
      const make = isAr
        ? booking.rentalCar?.car?.make?.makeAr || booking.car?.make?.makeAr || booking.rentalCar?.car?.make?.make || booking.car?.make?.make || ''
        : booking.rentalCar?.car?.make?.make || booking.car?.make?.make || ''
      const model = isAr
        ? booking.rentalCar?.car?.model?.modalAr || booking.car?.model?.modalAr || booking.rentalCar?.car?.model?.modal || booking.car?.model?.modal || ''
        : booking.rentalCar?.car?.model?.modal || booking.car?.model?.modal || ''
      const title = _.trim(`${make} ${model}`)
      return title || '—'
    },
    [i18n.language]
  )

  const CustomEvent = ({ event }) => {
    return (
      <div className="calendar-event">
        <div className="event-title flex justify-between items-center">
          <span>{event.title}</span>
          <span className="ml-2 text-xs text-gray-200">
            {event.resource.utcStart.format('h:mm A')} - {event.resource.utcEnd.format('h:mm A')}
          </span>
        </div>
        <div className="event-details">
          <small></small>
        </div>
      </div>
    )
  }

  const BookingDetailsModal = () => {
    if (!selectedBooking) return null

    // Get make and model based on language
    const isArabic = i18n.language === 'ar'
    const make = isArabic ? selectedBooking.car?.make?.makeAr || selectedBooking.car?.make?.make || '' : selectedBooking.car?.make?.make || ''
    const model = isArabic ? selectedBooking.car?.model?.modalAr || selectedBooking.car?.model?.modal || '' : selectedBooking.car?.model?.modal || ''

    // Get market segment based on language
    const marketSegment = isArabic ? selectedBooking.car?.marketSegmentAr || selectedBooking.car?.marketSegment || '' : selectedBooking.car?.marketSegment || ''

    // Get shifter translation
    const shifter = selectedBooking.car?.shifter
    const shifterText = shifter ? (isArabic ? (toLower(shifter) === 'automatic' ? 'أوتوماتيكي' : 'يدوي') : shifter) : ''

    // Get payment status translation
    const paymentStatus = toLower(selectedBooking.paymentStatus)
    const paymentStatusText = paymentStatus ? t(`bookings.paymentStatus.${paymentStatus}`, paymentStatus) : ''

    // Get payment method translation
    const paymentMethod = toLower(selectedBooking.paymentMethod)
    const paymentMethodText = paymentMethod ? t(`bookings.paymentMethod.${paymentMethod}`, paymentMethod) : ''

    // Get created by translation
    const createdBy = toLower(selectedBooking.bookingCreatedBy)
    const createdByText = createdBy ? t(`bookings.createdBy.${createdBy}`, createdBy) : ''

    // Get booking status translation
    const bookingStatus = toLower(selectedBooking.status)
    const bookingStatusText = bookingStatus ? t(`bookings.status.${bookingStatus}`, bookingStatus) : ''

    const statusBadgeClass = classNames(
      'inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1 ring-inset',
      selectedBooking.status === 'confirmed' && 'bg-emerald-50 text-emerald-900 ring-emerald-200/80',
      selectedBooking.status === 'pending' && 'bg-amber-50 text-amber-900 ring-amber-200/80',
      !_.includes(['confirmed', 'pending'], selectedBooking.status) && 'bg-slate-100 text-slate-800 ring-slate-200/80'
    )

    const labelClass = 'text-[11px] font-bold uppercase tracking-wider text-slate-500'
    const valueClass = 'mt-0.5 text-sm font-medium text-slate-900'

    return (
      <div className="space-y-4">
        <div className={classNames(modalSectionClass, 'border-[#091c2d]/10 bg-gradient-to-br from-slate-50/90 to-white')}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className={labelClass}>{t('booking.labels.status')}</p>
              <span className={classNames('mt-2', statusBadgeClass)}>{bookingStatusText}</span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <button
                type="button"
                onClick={() => history.push(`/booking/${selectedBooking._id}`)}
                className="inline-flex items-center justify-center rounded-xl bg-[#091c2d] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#142a3f] focus:outline-none focus:ring-2 focus:ring-[#091c2d] focus:ring-offset-2"
              >
                {t('companyDashboard.buttons.viewBooking')}
              </button>
              {selectedBooking?.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleUpdateBooking(selectedBooking._id, 'confirmed')}
                    type="button"
                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                    disabled={acceptLoading || rejectLoading}
                  >
                    {acceptLoading ? t('companyDashboard.buttons.accepting') : t('companyDashboard.buttons.acceptBooking')}
                  </button>
                  <button
                    onClick={() => handleUpdateBooking(selectedBooking._id, 'rejected')}
                    type="button"
                    className="inline-flex items-center justify-center rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2"
                    disabled={rejectLoading || acceptLoading}
                  >
                    {rejectLoading ? t('companyDashboard.buttons.rejecting') : t('companyDashboard.buttons.rejectBooking')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={modalSectionClass}>
          <h3 className={modalHeadingClass}>{t('companyDashboard.modal.vehicleDetails')}</h3>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              [t('companyDashboard.modal.make'), make],
              [t('companyDashboard.modal.model'), model],
              [t('companyDashboard.modal.marketSegment'), marketSegment],
              [t('companyDashboard.modal.engineSize'), selectedBooking.car?.engineSize || '—'],
              [t('companyDashboard.modal.numberOfSeats'), selectedBooking.car?.numberOfSeats || '—'],
              [t('companyDashboard.modal.shifter'), shifterText || '—'],
            ].map(([k, v], i) => (
              <div key={i} className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
                <dt className={labelClass}>{k}</dt>
                <dd className={valueClass}>{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className={modalSectionClass}>
          <h3 className={modalHeadingClass}>{t('companyDashboard.modal.bookingPeriod')}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.startDate')}</p>
              <p className="mt-1 text-sm font-medium text-slate-800">{moment.utc(selectedBooking.rentalStartDate).format('MMMM DD, YYYY h:mm A')}</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.endDate')}</p>
              <p className="mt-1 text-sm font-medium text-slate-800">{moment.utc(selectedBooking.rentalEndDate).format('MMMM DD, YYYY h:mm A')}</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100 sm:col-span-1">
              <p className={labelClass}>{t('companyDashboard.modal.bookingDate')}</p>
              <p className="mt-1 text-sm font-medium text-slate-800">{moment.utc(selectedBooking.bookingDate).format('MMMM DD, YYYY h:mm A')}</p>
            </div>
          </div>
        </div>

        <div className={modalSectionClass}>
          <h3 className={modalHeadingClass}>{t('companyDashboard.modal.branchInformation')}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.branchName')}</p>
              <p className={valueClass}>{selectedBooking.branch?.name || '—'}</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.timeZone')}</p>
              <p className={valueClass}>{selectedBooking.branch?.timeZone || '—'}</p>
            </div>
          </div>
        </div>

        <div className={modalSectionClass}>
          <h3 className={modalHeadingClass}>{t('companyDashboard.modal.paymentDetails')}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100 sm:col-span-2">
              <p className={labelClass}>{t('companyDashboard.modal.paymentStatus')}</p>
              <span
                className={classNames(
                  'mt-2 inline-flex rounded-lg px-2.5 py-1 text-sm font-semibold',
                  selectedBooking.paymentStatus === 'paid' ? 'bg-emerald-100 text-emerald-900' : 'bg-rose-100 text-rose-900'
                )}
              >
                {paymentStatusText}
              </span>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.paymentMethod')}</p>
              <p className={valueClass}>{paymentMethodText || '—'}</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.createdBy')}</p>
              <p className={valueClass}>{createdByText || '—'}</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.totalPrice')}</p>
              <p className={valueClass}>
                {selectedBooking.totalPrice || '0.00'} {t('common.omr')}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.dailyRate')}</p>
              <p className={valueClass}>
                {selectedBooking.dailyRate || '0.00'} {t('common.omr')}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.vatTax')}</p>
              <p className={valueClass}>{selectedBooking.vatTax ? parseFloat(selectedBooking.vatTax).toFixed(3) : '0.000'}%</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.insurance')}</p>
              <p className={valueClass}>{selectedBooking.insurance ? t('companyDashboard.modal.yes') : t('companyDashboard.modal.no')}</p>
            </div>
          </div>
        </div>

        <div className={modalSectionClass}>
          <h3 className={modalHeadingClass}>{t('companyDashboard.modal.additionalInformation')}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100 sm:col-span-2">
              <p className={labelClass}>{t('companyDashboard.modal.bookingId')}</p>
              <p className="mt-1 break-all font-mono text-xs text-slate-700">{selectedBooking._id}</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.carIndex')}</p>
              <p className={valueClass}>{selectedBooking.car?.index ?? '—'}</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100">
              <p className={labelClass}>{t('companyDashboard.modal.isReviewed')}</p>
              <p className={valueClass}>{selectedBooking.isReviewed ? t('companyDashboard.modal.yes') : t('companyDashboard.modal.no')}</p>
            </div>
            <div className="rounded-lg bg-slate-50/80 px-3 py-2 ring-1 ring-slate-100 sm:col-span-2">
              <p className={labelClass}>{t('companyDashboard.modal.createdAt')}</p>
              <p className="mt-1 text-sm font-medium text-slate-800">{moment.utc(selectedBooking.createdAt).format('MMMM DD, YYYY h:mm A')}</p>
            </div>
          </div>
        </div>

        {selectedBooking.notes && selectedBooking.notes.length > 0 && (
          <div className={modalSectionClass}>
            <h3 className={modalHeadingClass}>{t('companyDashboard.modal.notes')}</h3>
            <div className="space-y-2">
              {selectedBooking.notes.map((note, index) => (
                <div key={note._id || index} className="rounded-lg border border-slate-200/80 bg-slate-50/90 px-3 py-3">
                  <p className="text-sm leading-relaxed text-slate-800">{note.note}</p>
                  <p className="mt-2 text-xs font-medium text-slate-500">{moment.utc(note.updatedAt).format('MMMM DD, YYYY h:mm A')}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const [BookingModal, openBookingModal] = useModal({
    content: <BookingDetailsModal />,
    title: t('companyDashboard.modal.title'),
    closeable: true,
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '800px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const handleQueueBookingClick = booking => {
    const { queueMeta: _qm, ...rest } = booking
    setSelectedBooking(rest)
    openBookingModal()
  }

  const queueActionBookings = useMemo(() => bookingQueue.filter(b => b.queueMeta?.segment === 'action_required'), [bookingQueue])
  const queuePaidBookings = useMemo(() => bookingQueue.filter(b => b.queueMeta?.segment === 'paid_upcoming'), [bookingQueue])
  const queueNewBookings = useMemo(() => bookingQueue.filter(b => b.queueMeta?.isNew), [bookingQueue])

  const renderQueueBookingRow = useCallback(
    b => {
      const ps = toLower(b.paymentStatus || '')
      const paymentLabel = ps ? t(`bookings.paymentStatus.${ps}`, { defaultValue: b.paymentStatus }) : '—'
      const st = toLower(b.status || '')
      const statusLabel = st ? t(`bookings.status.${st}`, { defaultValue: b.status }) : '—'
      return (
        <li key={b._id}>
          <button
            type="button"
            onClick={() => handleQueueBookingClick(b)}
            className="flex w-full flex-col gap-2 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-slate-900 sm:text-sm">{getCarDisplayName(b)}</p>
              <p className="mt-0.5 text-[10px] text-slate-500 sm:text-xs">
                <span className="font-semibold text-slate-600">{t('companyDashboard.queueSection.rentalStart')}:</span>{' '}
                {b.rentalStartDate ? moment.utc(b.rentalStartDate).format('lll') : '—'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-800 ring-1 ring-slate-200/80 sm:text-[11px]">
                {statusLabel}
              </span>
              <span className="inline-flex rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 ring-1 ring-slate-200/80 sm:text-[11px]">{paymentLabel}</span>
            </div>
          </button>
        </li>
      )
    },
    [t, getCarDisplayName, handleQueueBookingClick]
  )

  if (loading) {
    return (
      <Layout background="#f1f5f9" title="">
        <PageContent>
          <div className="mx-auto flex min-h-[50vh] max-w-6xl flex-col items-center justify-center px-4 py-16">
            <div className="text-center">
              <div className="inline-flex justify-center">
                <LoadingIcon className="!ml-0 !mr-0 h-10 w-10 text-dark-600" />
              </div>
              <p className="mt-4 text-sm font-medium text-slate-600">{t('common.loading')}</p>
            </div>
          </div>
        </PageContent>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout background="#f1f5f9" title="">
        <PageContent>
          <div className="mx-auto max-w-lg px-4 py-12">
            <div className="rounded-2xl border border-rose-200/80 bg-white p-8 shadow-sm">
              <Alert type="error" message={error} />
              <button
                type="button"
                onClick={fetchBookings}
                className="mt-6 w-full rounded-xl bg-[#091c2d] px-4 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-[#142a3f] focus:outline-none focus:ring-2 focus:ring-[#091c2d] focus:ring-offset-2"
              >
                {t('companyDashboard.buttons.retry')}
              </button>
            </div>
          </div>
        </PageContent>
      </Layout>
    )
  }

  return (
    <Layout background="#f1f5f9" title="">
      <PageContent>
        <div className="mx-auto max-w-[1600px] px-4 pb-10 pt-4 sm:px-6 lg:px-8 lg:pb-8 lg:pt-6">
          <header className="relative mb-8 overflow-hidden rounded-2xl border border-slate-200/60 bg-gradient-to-br from-[#091c2d] via-[#142a3f] to-[#1e3a52] px-6 py-8 shadow-lg sm:px-10 sm:py-10">
            <div aria-hidden className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-teal-400/10 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -bottom-20 left-1/4 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
            <div className="relative z-[1]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">{t('companyDashboard.title')}</p>
              <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">{companyDisplayName}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/75 sm:text-base">{t('companyDashboard.description')}</p>
            </div>
          </header>

          {dashboardStats?.companyStatus?.needsReview && (
            <div className="mb-6 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900">
              <p className="font-semibold">{t('companySignup.review.companyTitle')}</p>
              <p>{t('companySignup.review.companyBody')}</p>
            </div>
          )}

          {dashboardStats ? (
            <div className="mb-8 space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
                {[
                  {
                    label: t('companyDashboard.statsDetail.totalBookings'),
                    value: dashboardStats.bookings?.total ?? 0,
                    accent: 'from-slate-700 to-slate-900',
                  },
                  {
                    label: t('companyDashboard.statsDetail.companyStaff'),
                    value: dashboardStats.companyUsers ?? 0,
                    accent: 'from-indigo-500 to-violet-600',
                  },
                  {
                    label: t('companyDashboard.statsDetail.uniqueBookers'),
                    value: dashboardStats.uniqueCustomerBookers ?? 0,
                    accent: 'from-sky-500 to-blue-600',
                  },
                  {
                    label: t('companyDashboard.statsDetail.totalCars'),
                    value: dashboardStats.cars?.total ?? 0,
                    accent: 'from-emerald-500 to-teal-600',
                  },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="group relative overflow-hidden rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm transition-all duration-300 hover:border-slate-300 hover:shadow-md sm:p-5"
                  >
                    <div className={classNames('mb-3 h-1 w-10 rounded-full bg-gradient-to-r', stat.accent)} aria-hidden />
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{stat.label}</p>
                    <p className="mt-1 text-2xl font-extrabold tabular-nums text-slate-900 sm:text-3xl">{stat.value}</p>
                  </div>
                ))}
              </div>

              {(() => {
                const evaluation = dashboardStats.evaluation
                const isSuperCompany = Boolean(userFromStore?.superCompanyUser)
                const scopeLabel = isSuperCompany
                  ? companyBranchFilterId
                    ? t('companyDashboard.evaluation.scopeFilteredBranch')
                    : t('companyDashboard.evaluation.scopeCompany')
                  : t('companyDashboard.evaluation.scopeBranch')
                const hasEvaluation = evaluation && evaluation.count > 0 && evaluation.percentage != null
                const verdictTone =
                  evaluation?.verdict === 'Excellent'
                    ? 'emerald'
                    : evaluation?.verdict === 'Good'
                    ? 'sky'
                    : evaluation?.verdict === 'Acceptable'
                    ? 'amber'
                    : evaluation?.verdict === 'Rejected'
                    ? 'rose'
                    : 'slate'

                return (
                  <div
                    className={classNames(
                      'overflow-hidden rounded-2xl border bg-white shadow-md ring-1 transition-shadow hover:shadow-lg',
                      evaluation?.isLow ? 'border-rose-200/90 ring-rose-900/[0.06]' : 'border-slate-200/90 ring-slate-900/[0.06]'
                    )}
                  >
                    <div className="relative flex items-center gap-3 border-b border-slate-200/70 bg-gradient-to-r from-slate-50/95 to-white px-4 py-4 sm:px-5">
                      <div
                        aria-hidden
                        className={classNames(
                          'pointer-events-none absolute inset-0 bg-gradient-to-r to-transparent',
                          evaluation?.isLow ? 'from-rose-500/[0.08]' : 'from-teal-500/[0.06]'
                        )}
                      />
                      <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#091c2d] to-[#1e3a52] text-white shadow-inner ring-1 ring-white/10">
                        <BadgeCheckIcon className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                      <div className="relative min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('companyDashboard.evaluation.title')}</p>
                        <p className="mt-0.5 text-xs font-semibold text-slate-600">{t('companyDashboard.evaluation.hint')}</p>
                      </div>
                      <span className="relative shrink-0 rounded-lg bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200/80">{scopeLabel}</span>
                    </div>
                    <div className="p-4 sm:p-5">
                      {!hasEvaluation ? (
                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500">
                          {t('companyDashboard.evaluation.empty')}
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50/80 px-4 py-3">
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t('companyDashboard.evaluation.percentage')}</p>
                              <p className="mt-1 text-3xl font-extrabold tabular-nums text-slate-900">{evaluation.percentage}%</p>
                            </div>
                            <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50/80 px-4 py-3">
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t('companyDashboard.evaluation.verdict')}</p>
                              <p className="mt-2">
                                <span
                                  className={classNames('inline-flex items-center rounded-lg px-2.5 py-1 text-sm font-bold ring-1', (statTone[verdictTone] || statTone.slate).pill)}
                                >
                                  {t(`officeEvaluation.verdict.${evaluation.verdict}`, { defaultValue: evaluation.verdict })}
                                </span>
                              </p>
                            </div>
                            <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50/80 px-4 py-3">
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t('companyDashboard.evaluation.evaluatedLabel')}</p>
                              <p className="mt-1 text-3xl font-extrabold tabular-nums text-slate-900">{evaluation.count}</p>
                            </div>
                          </div>
                          {evaluation.isLow ? (
                            <div className="mt-4">
                              <Alert type="warning" message={t('companyDashboard.evaluation.lowWarning')} showIcon />
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                )
              })()}

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-900/[0.06] transition-shadow hover:shadow-lg">
                  <div className="relative flex items-center gap-3 border-b border-slate-200/70 bg-gradient-to-r from-slate-50/95 to-white px-4 py-4 sm:px-5">
                    <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-r from-emerald-500/[0.06] to-transparent" />
                    <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#091c2d] to-[#1e3a52] text-white shadow-inner ring-1 ring-white/10">
                      <TruckIcon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="relative min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('companyDashboard.statsDetail.fleetByStatus')}</p>
                      <p className="mt-0.5 text-xs font-semibold text-slate-600">{t('companyDashboard.statsDetail.fleetCardHint')}</p>
                    </div>
                  </div>
                  <div className="space-y-2 p-3 sm:p-4">
                    {[
                      ['active', t('cars.status.active')],
                      ['booked', t('cars.status.booked')],
                      ['out of service', t('cars.status.outOfService')],
                      ['inProgress', t('cars.status.inProgress')],
                    ].map(([key, lbl]) => (
                      <DashboardStatRow key={key} label={lbl} value={dashboardStats.cars?.byStatus?.[key] ?? 0} tone={fleetKeyTone(key)} />
                    ))}
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-900/[0.06] transition-shadow hover:shadow-lg">
                  <div className="relative flex items-center gap-3 border-b border-slate-200/70 bg-gradient-to-r from-slate-50/95 to-white px-4 py-4 sm:px-5">
                    <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-r from-teal-500/[0.06] to-transparent" />
                    <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#091c2d] to-[#1e3a52] text-white shadow-inner ring-1 ring-white/10">
                      <ClipboardListIcon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="relative min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('companyDashboard.statsDetail.bookingsByStatus')}</p>
                      <p className="mt-0.5 text-xs font-semibold text-slate-600">{t('companyDashboard.statsDetail.bookingsCardHint')}</p>
                    </div>
                  </div>
                  <div className="space-y-2 p-3 sm:p-4">
                    {[
                      ['pending', t('companyDashboard.status.pending')],
                      ['confirmed', t('companyDashboard.status.confirmed')],
                      ['completed', t('companyDashboard.status.completed')],
                      ['rejected', t('companyDashboard.status.rejected')],
                      ['cancelled', t('bookings.status.cancelled')],
                      ['inProgress', t('bookings.status.inProgress')],
                    ].map(([key, lbl]) => (
                      <DashboardStatRow key={key} label={lbl} value={dashboardStats.bookings?.byStatus?.[key] ?? 0} tone={bookingKeyTone(key)} />
                    ))}
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-900/[0.06] transition-shadow hover:shadow-lg">
                  <div className="relative flex items-center gap-3 border-b border-slate-200/70 bg-gradient-to-r from-slate-50/95 to-white px-4 py-4 sm:px-5">
                    <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-r from-violet-500/[0.06] to-transparent" />
                    <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#091c2d] to-[#1e3a52] text-white shadow-inner ring-1 ring-white/10">
                      <CurrencyDollarIcon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="relative min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('companyDashboard.statsDetail.paymentBreakdown')}</p>
                      <p className="mt-0.5 text-xs font-semibold text-slate-600">{t('companyDashboard.statsDetail.paymentsCardHint')}</p>
                    </div>
                  </div>
                  <div className="space-y-2 p-3 sm:p-4">
                    {_.keys(dashboardStats.bookings?.byPaymentStatus || {}).length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500">
                        {t('companyDashboard.statsDetail.noPaymentData')}
                      </div>
                    ) : (
                      Object.entries(dashboardStats.bookings.byPaymentStatus).map(([key, val]) => (
                        <DashboardStatRow key={key} label={t(`bookings.paymentStatus.${key}`, { defaultValue: key })} value={val} tone={paymentKeyTone(key)} />
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-8 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-10 text-center text-sm text-slate-500">
              {t('companyDashboard.statsDetail.loading')}
            </div>
          )}

          <div className="mb-8">
            <div className="mb-4 px-1">
              <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">{t('companyDashboard.queueSection.title')}</h2>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">{t('companyDashboard.queueSection.subtitle')}</p>
              {queueMeta && !queueLoading ? (
                <p className="mt-2 text-xs font-medium text-slate-500">
                  {t('companyDashboard.queueSection.metaLine', {
                    newCount: queueMeta.newCount ?? 0,
                    actionCount: queueMeta.actionRequiredCount ?? 0,
                  })}
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {[
                {
                  key: 'action',
                  list: queueActionBookings,
                  titleKey: 'cardActionTitle',
                  subKey: 'cardActionSubtitle',
                  emptyKey: 'emptyAction',
                  ring: 'ring-amber-200/60',
                  headBg: 'bg-amber-50/90',
                  borderAccent: 'border-amber-200/80',
                },
                {
                  key: 'paid',
                  list: queuePaidBookings,
                  titleKey: 'cardPaidTitle',
                  subKey: 'cardPaidSubtitle',
                  emptyKey: 'emptyPaid',
                  ring: 'ring-emerald-200/60',
                  headBg: 'bg-emerald-50/90',
                  borderAccent: 'border-emerald-200/80',
                },
                {
                  key: 'new',
                  list: queueNewBookings,
                  titleKey: 'cardNewTitle',
                  subKey: 'cardNewSubtitle',
                  emptyKey: 'emptyNew',
                  ring: 'ring-sky-200/60',
                  headBg: 'bg-sky-50/90',
                  borderAccent: 'border-sky-200/80',
                  isNewCard: true,
                },
              ].map(card => (
                <div
                  key={card.key}
                  className={classNames('flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm ring-1 ring-slate-900/[0.04]', card.borderAccent, card.ring)}
                >
                  <div className={classNames('border-b px-4 py-3 sm:px-4 sm:py-3.5', card.headBg, card.borderAccent)}>
                    <h3 className="text-sm font-bold text-slate-900">{t(`companyDashboard.queueSection.${card.titleKey}`)}</h3>
                    <p className="mt-0.5 text-[11px] leading-snug text-slate-600">
                      {card.isNewCard
                        ? t(`companyDashboard.queueSection.${card.subKey}`, {
                            hours: queueMeta?.newWithinHours ?? 72,
                          })
                        : t(`companyDashboard.queueSection.${card.subKey}`)}
                    </p>
                    {!queueLoading ? (
                      <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-500">
                        {t('companyDashboard.queueSection.countLabel', { count: card.list.length })}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex min-h-[140px] flex-1 flex-col overflow-hidden">
                    {queueLoading ? (
                      <div className="flex flex-1 items-center justify-center py-10">
                        <LoadingIcon className="!ml-0 !mr-0 h-8 w-8 text-dark-600" />
                      </div>
                    ) : card.list.length === 0 ? (
                      <p className="flex flex-1 items-center justify-center px-3 py-8 text-center text-xs text-slate-500">{t(`companyDashboard.queueSection.${card.emptyKey}`)}</p>
                    ) : (
                      <ul className="max-h-[min(320px,50vh)] divide-y divide-slate-100 overflow-y-auto overscroll-contain px-1 py-1">
                        {card.list.map(b => renderQueueBookingRow(b))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {bookings.length === 0 && (
            <div className="mb-6 rounded-xl border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-[13px] text-amber-950 ring-1 ring-amber-100/80">
              {t('companyDashboard.emptyCalendar')}
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
            <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-5 sm:px-6 sm:py-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">{t('companyDashboard.activeBookingsCalendar')}</h2>
                  <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('companyDashboard.calendarSectionSubtitle')}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t('companyDashboard.legend')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-[#64748b]" aria-hidden />
                      {t('companyDashboard.status.pending')}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-[#059669]" aria-hidden />
                      {t('companyDashboard.status.confirmed')}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-[#0d9488]" aria-hidden />
                      {t('companyDashboard.status.completed')}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-[#dc2626]" aria-hidden />
                      {t('companyDashboard.status.rejected')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="hidden sm:block" style={{ height: '600px' }}>
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  onSelectEvent={handleSelectEvent}
                  onSelectSlot={handleSelectSlot}
                  selectable
                  eventPropGetter={eventStyleGetter}
                  components={{
                    event: CustomEvent,
                  }}
                  views={['month', 'week', 'day', 'agenda']}
                  defaultView="month"
                  step={60}
                  timeslots={1}
                  showMultiDayTimes
                  popupOffset={30}
                  messages={calendarMessages}
                />
              </div>

              <div className="block sm:hidden overflow-y-auto" style={{ height: '400px' }}>
                <div style={{ minHeight: '600px' }}>
                  <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectable
                    eventPropGetter={eventStyleGetter}
                    components={{
                      event: CustomEvent,
                    }}
                    views={['month', 'week', 'day', 'agenda']}
                    defaultView="month"
                    step={60}
                    timeslots={1}
                    showMultiDayTimes
                    popupOffset={30}
                    messages={calendarMessages}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <BookingModal />
        <style jsx>{`
          .calendar-event {
            padding: 2px 4px;
            font-size: 11px;
            line-height: 1.2;
            height: auto;
            min-height: 20px;
            margin-bottom: 2px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .event-title {
            font-weight: bold;
            margin-bottom: 1px;
            font-size: 11px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .event-details {
            opacity: 0.9;
            font-size: 10px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .rbc-event {
            border-radius: 4px;
            margin-bottom: 2px;
            height: auto;
            min-height: 20px;
          }

          .rbc-event-content {
            padding: 2px 4px;
            height: auto;
            min-height: 20px;
            overflow: hidden;
          }

          .rbc-event-overlaps {
            margin-right: 0;
          }

          .rbc-addons-dnd .rbc-event {
            margin-bottom: 2px;
          }

          .rbc-month-view .rbc-event {
            margin-bottom: 2px;
          }

          .rbc-month-view .rbc-date-cell {
            overflow: visible;
          }

          .rbc-month-view .rbc-events-container {
            margin-right: 0;
          }

          /* RTL Support for Arabic */
          [dir='rtl'] .rbc-toolbar {
            direction: rtl;
          }

          [dir='rtl'] .rbc-toolbar .rbc-toolbar-label {
            order: 2;
            margin: 0 8px;
          }

          [dir='rtl'] .rbc-btn-group {
            order: 1;
          }

          [dir='rtl'] .rbc-toolbar .rbc-btn-group:last-child {
            order: 3;
          }

          [dir='rtl'] .rbc-btn-group button {
            border-radius: 4px;
            margin: 0 2px;
            padding: 6px 12px;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
            border: 1px solid #d1d5db;
            background: white;
            color: #374151;
          }

          [dir='rtl'] .rbc-btn-group button:hover {
            background: #f3f4f6;
            border-color: #9ca3af;
          }

          [dir='rtl'] .rbc-btn-group button.rbc-active {
            background: #3b82f6;
            color: white;
            border-color: #3b82f6;
          }

          [dir='rtl'] .rbc-btn-group button:first-child {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
          }

          [dir='rtl'] .rbc-btn-group button:last-child {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
          }

          [dir='rtl'] .rbc-btn-group button:not(:first-child):not(:last-child) {
            border-radius: 0;
          }

          /* Navigation buttons styling for RTL */
          [dir='rtl'] .rbc-toolbar .rbc-btn-group:first-child button {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            color: #475569;
            font-weight: 600;
          }

          [dir='rtl'] .rbc-toolbar .rbc-btn-group:first-child button:hover {
            background: #e2e8f0;
            border-color: #cbd5e1;
          }

          [dir='rtl'] .rbc-toolbar .rbc-btn-group:first-child button:active {
            background: #cbd5e1;
            transform: translateY(1px);
          }

          /* View buttons styling for RTL */
          [dir='rtl'] .rbc-toolbar .rbc-btn-group:last-child button {
            background: #ffffff;
            border: 1px solid #d1d5db;
            color: #6b7280;
            font-weight: 500;
          }

          [dir='rtl'] .rbc-toolbar .rbc-btn-group:last-child button:hover {
            background: #f9fafb;
            border-color: #9ca3af;
            color: #374151;
          }

          [dir='rtl'] .rbc-toolbar .rbc-btn-group:last-child button.rbc-active {
            background: #1f2937;
            color: white;
            border-color: #1f2937;
            font-weight: 600;
          }

          /* Mobile and tablet responsive styles - only apply scrolling on small screens */
          @media (max-width: 768px) {
            .rbc-toolbar {
              flex-direction: column;
              gap: 8px;
              position: sticky;
              top: 0;
              background: white;
              z-index: 10;
              padding: 8px;
              margin: -8px -8px 8px -8px;
              border-bottom: 1px solid #e5e7eb;
            }

            .rbc-toolbar-label {
              font-size: 14px;
            }

            .rbc-btn-group {
              display: flex;
              flex-wrap: wrap;
              gap: 4px;
            }

            .rbc-btn-group button {
              font-size: 12px;
              padding: 4px 8px;
            }

            .rbc-month-view .rbc-date-cell {
              min-height: 60px;
            }

            .rbc-month-view .rbc-events-container {
              margin-right: 0;
            }

            .rbc-event {
              font-size: 10px;
              padding: 1px 2px;
            }

            .rbc-month-view {
              height: auto !important;
            }

            .rbc-month-row {
              height: auto !important;
            }

            .rbc-calendar {
              height: auto !important;
              overflow: visible !important;
            }

            .rbc-month-view {
              height: auto !important;
              overflow: visible !important;
            }

            .rbc-month-row {
              height: auto !important;
              min-height: 80px;
            }

            /* RTL mobile adjustments */
            [dir='rtl'] .rbc-toolbar {
              direction: rtl;
            }

            [dir='rtl'] .rbc-toolbar .rbc-toolbar-label {
              order: 2;
              text-align: center;
            }

            [dir='rtl'] .rbc-btn-group {
              order: 1;
              justify-content: center;
            }

            [dir='rtl'] .rbc-toolbar .rbc-btn-group:last-child {
              order: 3;
              justify-content: center;
            }

            [dir='rtl'] .rbc-btn-group button {
              border-radius: 6px;
              margin: 0 1px;
              padding: 6px 10px;
              font-size: 12px;
            }
          }

          @media (max-width: 640px) {
            .rbc-toolbar {
              padding: 8px;
            }

            .rbc-toolbar-label {
              font-size: 12px;
              margin-bottom: 4px;
            }

            .rbc-btn-group {
              justify-content: center;
            }

            .rbc-btn-group button {
              font-size: 10px;
              padding: 2px 6px;
            }

            .rbc-month-view .rbc-date-cell {
              min-height: 50px;
            }

            .rbc-event {
              font-size: 9px;
              padding: 1px;
            }

            /* RTL small mobile adjustments */
            [dir='rtl'] .rbc-btn-group button {
              font-size: 10px;
              padding: 4px 8px;
              border-radius: 4px;
            }
          }
        `}</style>
      </PageContent>
    </Layout>
  )
}

export default CompanyDashboard
